import { useNavigate } from "@tanstack/solid-router";
import { For, Show, createSignal } from "solid-js";

import { apiRequest } from "~/lib/api";
import { useAccounts, useMergeAccount } from "~/lib/hooks";
import type { AccountRead } from "~/lib/types";

type Props = {
  wsKey: () => string;
  // The account being viewed — it's the doomed in the merge (survivor is picked).
  doomed: AccountRead;
  onClose: () => void;
};

// "Merge into another account" — the account currently being viewed is the
// DOOMED side (its row gets archived, references repointed to the survivor).
// We pick the SURVIVOR from a search picker, then a confirm step shows the
// diff side-by-side.
export function MergeAccountDialog(props: Props) {
  const navigate = useNavigate();
  const mergeMutation = useMergeAccount(props.wsKey);

  const [query, setQuery] = createSignal("");
  const [survivor, setSurvivor] = createSignal<AccountRead | null>(null);
  const [err, setErr] = createSignal<string | null>(null);

  // Picker results: filter out the current account and any archived ones.
  const search = useAccounts(props.wsKey, () => ({
    q: query() || undefined,
    limit: 12,
  }));

  const handleConfirm = async () => {
    const target = survivor();
    if (!target) return;
    setErr(null);
    try {
      await mergeMutation.mutateAsync({
        doomedAccId: props.doomed.id,
        intoAccountId: target.id,
      });
      props.onClose();
      void navigate({
        to: "/workspaces/$wsKey/accounts/$accId",
        params: { wsKey: props.wsKey(), accId: target.id },
      });
    } catch (e) {
      setErr((e as Error).message);
    }
  };

  return (
    <dialog open class="modal modal-open" aria-modal="true" aria-labelledby="merge-acc-title">
      <div class="modal-box max-w-2xl">
        <h3 id="merge-acc-title" class="font-bold text-lg mb-2">
          Merge "{props.doomed.name}" into…
        </h3>
        <p class="text-sm opacity-80 mb-3">
          The account you're viewing will be archived; its contacts, deals, notes,
          activities, and tags move to the account you pick. This can't be undone
          automatically.
        </p>

        <Show
          when={survivor()}
          fallback={
            <>
              <input
                type="search"
                class="input input-bordered w-full"
                placeholder="Search accounts by name, domain, email…"
                aria-label="Search accounts to merge into"
                value={query()}
                onInput={(e) => setQuery(e.currentTarget.value)}
                autofocus
              />
              <ul class="mt-2 max-h-64 overflow-y-auto divide-y divide-base-300">
                <For each={(search.data?.items ?? []).filter((a) => a.id !== props.doomed.id)}>
                  {(a) => (
                    <li>
                      <button
                        type="button"
                        class="w-full text-left px-3 py-2 hover:bg-base-200"
                        onClick={async () => {
                          // Hydrate the full record so we can show real field
                          // values in the confirm diff (the list shape is slim).
                          try {
                            const full = await apiRequest<AccountRead>(
                              `/v1/workspaces/${props.wsKey()}/accounts/${a.id}`,
                            );
                            setSurvivor(full);
                          } catch (e) {
                            setErr((e as Error).message);
                          }
                        }}
                      >
                        <div class="font-medium">{a.name}</div>
                        <div class="text-xs opacity-60 truncate">
                          {[a.primary_domain, a.primary_email].filter(Boolean).join(" · ") || "—"}
                        </div>
                      </button>
                    </li>
                  )}
                </For>
                <Show when={search.data && search.data.items.length === 0}>
                  <li class="px-3 py-3 text-sm opacity-60 text-center">
                    No accounts match.
                  </li>
                </Show>
              </ul>
            </>
          }
        >
          {(targetAcc) => (
            <div>
              <div class="grid grid-cols-2 gap-3 text-sm">
                <div class="bg-error/10 rounded-lg p-3">
                  <div class="text-xs uppercase opacity-60 mb-1">Going away</div>
                  <div class="font-medium">{props.doomed.name}</div>
                  <DiffRow label="Domain" value={props.doomed.primary_domain} />
                  <DiffRow label="Email" value={props.doomed.primary_email} />
                  <DiffRow label="Lifecycle" value={props.doomed.lifecycle_stage} />
                  <DiffRow
                    label="Owner"
                    value={props.doomed.owner_user_id ? "set" : null}
                  />
                </div>
                <div class="bg-success/10 rounded-lg p-3">
                  <div class="text-xs uppercase opacity-60 mb-1">Keeping</div>
                  <div class="font-medium">{targetAcc().name}</div>
                  <DiffRow label="Domain" value={targetAcc().primary_domain} />
                  <DiffRow label="Email" value={targetAcc().primary_email} />
                  <DiffRow label="Lifecycle" value={targetAcc().lifecycle_stage} />
                  <DiffRow
                    label="Owner"
                    value={targetAcc().owner_user_id ? "set" : null}
                  />
                </div>
              </div>
              <Show when={err()}>
                <div role="alert" class="alert alert-error text-sm mt-3">
                  {err()}
                </div>
              </Show>
            </div>
          )}
        </Show>

        <div class="modal-action">
          <button
            type="button"
            class="btn btn-ghost"
            onClick={props.onClose}
            disabled={mergeMutation.isPending}
          >
            Cancel
          </button>
          <Show when={survivor()}>
            <button
              type="button"
              class="btn btn-ghost"
              onClick={() => setSurvivor(null)}
              disabled={mergeMutation.isPending}
            >
              Back
            </button>
            <button
              type="button"
              class="btn btn-warning"
              onClick={handleConfirm}
              disabled={mergeMutation.isPending}
            >
              {mergeMutation.isPending ? "Merging…" : "Confirm merge"}
            </button>
          </Show>
        </div>
      </div>
    </dialog>
  );
}

function DiffRow(props: { label: string; value: string | null }) {
  return (
    <div class="text-xs mt-1">
      <span class="opacity-60">{props.label}:</span>{" "}
      <span class="font-medium">{props.value ?? "—"}</span>
    </div>
  );
}
